<template>
  <div class="relative h-full flex antialiased">
    <div class="relative flex-1 flex flex-col h-full pt-24 overflow-y-auto">
      <TransitionFadeSlideUp>
        <div v-show="focused" class="absolute top-0 right-0 z-20 flex items-center h-24 pr-6 text-grey-400" style="left: 140px;">
        </div>
      </TransitionFadeSlideUp>
      <div class="hidden lg:block">
        <TransitionHeight>
          <div v-show="!focused" class="overflow-hidden">
            <div class="flex flex-col items-center pt-10 pb-6">
              <SvgCompassLoader class="flex-shrink-0 h-24" />
              <h1 class="mt-4 text-4xl font-bold leading-none tracking-wide text-center uppercase font-header text-grey-500">Cost & Pricing<br>Calculator</h1>
              <h2 class="mt-4 text-lg font-light tracking-wider text-grey-400">Estimate your flight time with your favorite aircraft</h2>
            </div>
          </div>
        </TransitionHeight>
        <div class="flex">
          <Input
            label="From" ref="from" :focused="focusedInput === 'from'" @focus="handleFocusInput('from')" @blur="focusedInput = null"
            @clear="handleClearInput('from')" :selected-airport="fromAirport" v-model="fromInput" v-slot:input-right
            class="flex-1" :input-class="focusedInput !== 'from' ? 'border-r-0' : null" tabindex="0"
          >
            <CurrentLocation v-if="!fromAirport" @change="handleCurrentLocation" />
          </Input>
          <Input
            label="To" ref="to" :focused="focusedInput === 'to'" @focus="handleFocusInput('to')" @blur="focusedInput = null"
            @clear="handleClearInput('to')" :selected-airport="toAirport" v-model="toInput" class="flex-1"
            :input-class="focusedInput === 'from' ? 'border-l-0' : null" tabindex="0"
          />
        </div>
        <div class="relative">
          <TransitionFadeSlideUp>
            <div
              v-if="places.length || suggestedAirports.length"
              class="absolute inset-x-0 top-0 flex flex-col"
            >
              <PlaceList
                v-if="places.length"
                :other-airport="fromAirport || toAirport"
                :style="`order: ${order.indexOf('places')}`"
                @select="handleSelectAirport"
              />
              <ul
                v-if="suggestedAirports.length"
                class="border-b border-grey-200"
                :style="`order: ${order.indexOf('airports')}`"
              >
                <li class="px-4 py-1 text-sm font-medium tracking-wider uppercase bg-grey-100 text-grey-500">Airports</li>
                <AirportItem
                  v-for="(airport, index) in suggestedAirports"
                  :key="`airport-${index}`"
                  :airport="airport"
                  @select="handleSelectAirport"
                />
              </ul>
            </div>
            <SvgCompassLoader :animated="true" v-else-if="estimateLoading" class="w-16 mx-auto mt-16" />
            <Estimate
              v-else-if="Array.isArray(estimate) && estimate.length"
              :data="estimate"
              :slidesPerView="expanded ? 3 : 2"
              :swipable="!expanded"
              class="absolute inset-x-0 top-0 pb-6"
            />
            <div v-else-if="Array.isArray(estimate) && !estimate.length">
              <p class="mt-4 text-center text-grey-400">No results</p>
            </div>
            <div v-else-if="!this.fromAirport && focusedInput === 'from'" class="flex flex-col items-center">
              <SvgCompassLoader class="w-16 mt-16" />
              <p class="mt-4 text-grey-400">Please enter a departure airport</p>
            </div>
            <div v-else-if="!this.toAirport && focusedInput === 'to'" class="flex flex-col items-center">
              <SvgCompassLoader class="w-16 mt-16" />
              <p class="mt-4 text-grey-400">Please enter an arrival airport</p>
            </div>
          </TransitionFadeSlideUp>
        </div>
      </div>
    </div>
    <div
      class="w-1.5"
      style="background: linear-gradient(to bottom, #FAD0C7 0%, #FFD493 27.6%, #8ADAD5 100%)"
    />
    <button
      class="absolute bottom-0 bg-white right-0 h-8 w-8 flex justify-center items-center mb-4 mr-8 z-20 border border-cream-600 hover:border-cream-500 rounded-full text-black transition-all ease-out"
      @click="$emit('expandClick')"
    >
      <SvgChevronLeft
        width="7"
        :class="[
          'transition-300',
          expanded ? 'rotate-0 mr-px' : 'rotate-180 ml-px', 
        ]"
      />
    </button>
  </div>
</template>

<script>
import sidebarMixin from '@/mixins/sidebar'
import TransitionHeight from '@/components/transitions/Height'
import TransitionFadeSlideUp from '@/components/transitions/FadeSlideUp'
import SvgSearch from '@/components/svg/Search'
import SvgClose from '@/components/svg/Close'
import SvgChevronLeft from '@/components/svg/ChevronLeft'

export default {
  mixins: [sidebarMixin],
  components: { TransitionHeight, TransitionFadeSlideUp, SvgSearch, SvgClose, SvgChevronLeft },
  props: {
    expanded: { type: Boolean, required: true },
  },
  data () {
    return {
      lastFocusedInput: null,
      fromInput: null,
      toInput: null,
    }
  },
  computed: {
    focused () {
      return this.focusedInput || this.fromInput || this.toInput || this.fromAirport || this.toAirport || this.estimate || this.lastFocusedInput
    }
  },
  watch: {
    fromInput: {
      handler (val) {
        this.inputHandler('from', val)
      }
    },
    toInput: {
      handler (val) {
        this.inputHandler('to', val)
      }
    },
  },
  methods: {
    handleFocusInput (focused) {
      this.lastFocusedInput = focused
      this.focusedInput = focused
      const airport = this[`${focused}Airport`]
      if (airport && airport.name === this[`${focused}Input`] ) {
        this[`${focused}Input`] = null
      }
    },
    async handleSelectAirport (airport) {
      if (! (airport.is_open && (airport.jetfly_pc12_operability !== 'unoperable' || airport.jetfly_pc24_operability !== 'unoperable' || airport.jetfly_sf50_operability !== 'unoperable'))) return

      if (this.lastFocusedInput) {
        this[this.lastFocusedInput + 'Airport'] = airport
        this[this.lastFocusedInput + 'Input'] = null
      }

      this.places = []
      this.suggestedAirports = []
      this.order = []

      if (this.fromAirport && !this.toAirport) this.$refs.to.$el.children[1].focus()
      else if (this.toAirport && !this.fromAirport) this.$refs.from.$el.children[1].focus()
      else {
        this.$refs.to.$el.children[1].focus()
        this.$refs.to.$el.children[1].blur()
      }
    },
    handleClearInput (input) {
      this[input + 'Airport'] = null
      this[input + 'Input'] = ''

      if (!this.fromAirport && !this.toAirport) {
        this.focusedInput = null
        this.lastFocusedInput = null
      }
    },
  }
}
</script>
